// Lightweight dependency invalidation rules
// Defines which caches should be invalidated when entities change

import { multiTabCoordinator } from '../services/multi-tab-coordination.service.js';

// Invalidation rules: entity type → cache patterns to invalidate
const INVALIDATION_RULES = {
  athletes: [
    // Invalidate athlete-specific caches
    (entityId) => `athletes:${entityId}`,
    (entityId) => `athletes:list`,
    // Invalidate trainer's athlete lists
    (entityId) => `trainers:*:athletes`,
    // Invalidate dashboard aggregates
    () => `dashboard:*`,
    // Invalidate attendance if athlete data changed
    (entityId) => `attendance:*:${entityId}`,
    // Invalidate payments
    (entityId) => `payments:*:${entityId}`,
  ],
  
  trainers: [
    (entityId) => `trainers:${entityId}`,
    (entityId) => `trainers:list`,
    () => `dashboard:*`,
    // Invalidate trainer's athlete lists
    (entityId) => `trainers:${entityId}:athletes`,
  ],
  
  programs: [
    (entityId) => `programs:${entityId}`,
    (entityId) => `programs:list`,
    // Invalidate athlete program assignments
    (entityId) => `athletes:*:programs`,
  ],
  
  diets: [
    (entityId) => `diets:${entityId}`,
    (entityId) => `diets:list`,
    // Invalidate athlete diet assignments
    (entityId) => `athletes:*:diets`,
  ],
  
  daybooks: [
    (entityId) => `daybooks:${entityId}`,
    (entityId) => `daybooks:list`,
    // Invalidate athlete daybook lists
    (entityId) => `daybooks:*:${entityId}`,
    // Invalidate dashboard aggregates
    () => `dashboard:*`,
  ],
  
  payments: [
    (entityId) => `payments:${entityId}`,
    (entityId) => `payments:list`,
    // Invalidate athlete payment history
    (entityId) => `athletes:${entityId}:payments`,
    // Invalidate dashboard aggregates
    () => `dashboard:*`,
  ],
  
  attendance: [
    (entityId) => `attendance:${entityId}`,
    (entityId) => `attendance:list`,
    // Invalidate athlete attendance
    (entityId) => `athletes:${entityId}:attendance`,
    // Invalidate dashboard aggregates
    () => `dashboard:*`,
  ],
  
  gyms: [
    (entityId) => `gyms:${entityId}`,
    (entityId) => `gyms:list`,
    // Invalidate gym-specific data
    (entityId) => `gyms:${entityId}:*`,
    // Invalidate dashboard
    () => `dashboard:*`,
  ],
  
  memberships: [
    (entityId) => `memberships:${entityId}`,
    (entityId) => `memberships:list`,
    // Invalidate athlete membership
    (entityId) => `athletes:${entityId}:membership`,
  ],
  
  lockers: [
    (entityId) => `lockers:${entityId}`,
    (entityId) => `lockers:list`,
    // Invalidate locker assignments
    (entityId) => `lockers:*:assignments`,
  ],
  
  notifications: [
    (entityId) => `notifications:${entityId}`,
    (entityId) => `notifications:list`,
    // Notifications are ephemeral, invalidate broadly
    () => `notifications:*`,
  ],
};

class InvalidationManager {
  constructor() {
    this.pendingInvalidations = new Map();
    this.debounceTimer = null;
    this.debounceDelay = 500; // 500ms debounce
  }

  /**
   * Get invalidation patterns for entity type
   */
  getPatterns(entityType) {
    return INVALIDATION_RULES[entityType] || [];
  }

  /**
   * Generate cache keys to invalidate
   */
  generateCacheKeys(entityType, entityId) {
    const patterns = this.getPatterns(entityType);
    const keys = [];

    for (const pattern of patterns) {
      const key = pattern(entityId);
      if (key.includes('*')) {
        // Wildcard pattern - will need to be expanded
        keys.push({ pattern: key, isWildcard: true });
      } else {
        keys.push({ pattern: key, isWildcard: false });
      }
    }

    return keys;
  }

  /**
   * Invalidate caches for entity
   */
  async invalidate(entityType, entityId) {
    const cacheKeys = this.generateCacheKeys(entityType, entityId);
    
    console.log('[Invalidation] Invalidating for:', entityType, entityId, cacheKeys);

    // Debounce invalidations
    this.scheduleInvalidation(cacheKeys, entityType);
  }

  /**
   * Schedule batch invalidation with debounce
   */
  scheduleInvalidation(cacheKeys, entityType) {
    // Add to pending
    for (const key of cacheKeys) {
      const keyStr = JSON.stringify(key);
      this.pendingInvalidations.set(keyStr, key);
    }

    // Debounce
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(() => {
      this.executeBatchInvalidation(entityType);
    }, this.debounceDelay);
  }

  /**
   * Execute batch invalidation
   */
  async executeBatchInvalidation(entityType) {
    const keys = Array.from(this.pendingInvalidations.values());
    this.pendingInvalidations.clear();

    console.log('[Invalidation] Executing batch invalidation:', keys.length, 'keys');

    // Import repositories dynamically to avoid circular dependency
    const { cacheStore } = await import('../db/database.js');
    const { db } = await import('../db/database.js');

    let invalidatedCount = 0;

    for (const key of keys) {
      if (key.isWildcard) {
        // Expand wildcard and invalidate all matching keys
        const allKeys = await db.getKeys(cacheStore);
        const pattern = key.pattern.replace('*', '.*');
        const regex = new RegExp(`^${pattern}$`);
        
        const matchingKeys = allKeys.filter(k => regex.test(k));
        
        for (const matchKey of matchingKeys) {
          await db.delete(matchKey, cacheStore);
          invalidatedCount++;
        }
      } else {
        // Exact key match
        try {
          await db.delete(key.pattern, cacheStore);
          invalidatedCount++;
        } catch (error) {
          console.error('[Invalidation] Failed to delete:', key.pattern, error);
        }
      }
    }

    console.log('[Invalidation] Invalidated', invalidatedCount, 'cache entries');

    // Notify other tabs
    multiTabCoordinator.notifyCacheInvalidation(null, entityType);
  }

  /**
   * Invalidate by URL pattern
   */
  async invalidateByUrlPattern(urlPattern) {
    const { cacheStore } = await import('../db/database.js');
    const { db } = await import('../db/database.js');

    const allKeys = await db.getKeys(cacheStore);
    const regex = new RegExp(urlPattern);
    
    const matchingKeys = allKeys.filter(key => regex.test(key));
    
    for (const key of matchingKeys) {
      await db.delete(key, cacheStore);
    }

    console.log('[Invalidation] Invalidated', matchingKeys.length, 'entries by pattern:', urlPattern);
  }

  /**
   * Invalidate all caches for entity type
   */
  async invalidateEntityType(entityType) {
    const { cacheStore } = await import('../db/database.js');
    const { db } = await import('../db/database.js');

    const allKeys = await db.getKeys(cacheStore);
    const entityKeys = allKeys.filter(key => key.startsWith(`${entityType}:`));
    
    for (const key of entityKeys) {
      await db.delete(key, cacheStore);
    }

    console.log('[Invalidation] Invalidated all', entityType, 'caches:', entityKeys.length);

    // Notify other tabs
    multiTabCoordinator.notifyCacheInvalidation(null, entityType);
  }

  /**
   * Clear all pending invalidations
   */
  clearPending() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.pendingInvalidations.clear();
  }
}

// Export singleton instance
export const invalidationManager = new InvalidationManager();
